'use client';

import React, { useEffect, useRef, useState } from 'react';

const STORY_ITEMS = [
  { label: 'Sales', icon: '↑' },
  { label: 'Inventory', icon: '◻' },
  { label: 'Customers', icon: '◎' },
  { label: 'Purchases', icon: '⟳' },
  { label: 'Payments', icon: '₹' },
  { label: 'Production', icon: '⚙' },
];

const FLOW_STEPS = [
  { label: 'DATA', sub: 'Thousands of signals, every day.' },
  { label: 'UNDERSTANDING', sub: 'Patterns, context, relationships.' },
  { label: 'DECISION', sub: 'Know what to do next.' },
];

export default function ProblemSection() {
  const [visible, setVisible] = useState(false);
  const [flowVisible, setFlowVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          setTimeout(() => setFlowVisible(true), 600);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="problem"
      ref={sectionRef}
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #FBFAF6 0%, #F6F5F0 100%)' }}
      aria-label="The problem VINA solves"
    >
      {/* Ambient */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] opacity-15"
          style={{ background: 'radial-gradient(ellipse, rgba(31,77,63,0.08) 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
      </div>
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Headline */}
        <div className="text-center mb-16">
          <h2
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'clamp(2rem, 4.5vw, 3.2rem)',
              letterSpacing: '-0.03em',
              color: '#0D2620',
              lineHeight: 1.1,
              marginBottom: '20px',
            }}
          >
            Your business knows more than you do.
          </h2>
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
              color: '#3D5550',
              maxWidth: '520px',
              margin: '0 auto',
              lineHeight: 1.7,
            }}
          >
            Every day, your business creates thousands of signals.
          </p>
        </div>

        {/* Signal grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl mx-auto mb-16">
          {STORY_ITEMS?.map((item, i) => (
            <div
              key={item?.label}
              className="flex items-center gap-3 px-5 py-4 rounded-2xl"
              style={{
                background: 'rgba(255,255,255,0.75)',
                border: '1px solid rgba(211,223,219,0.7)',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(16px)',
                transition: `opacity 0.5s ease ${i * 80}ms, transform 0.5s cubic-bezier(0.22,1,0.36,1) ${i * 80}ms`,
              }}
            >
              <span style={{ fontSize: '16px', color: '#1F4D3F', fontWeight: 600, width: '20px', textAlign: 'center' }}>
                {item?.icon}
              </span>
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', fontWeight: 500, color: '#1A3530' }}>
                {item?.label}
              </span>
            </div>
          ))}
        </div>

        {/* Insight copy */}
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
              color: '#3D5550',
              lineHeight: 1.8,
            }}
          >
            But knowing something happened and knowing what to do about it are two different things.
          </p>
        </div>

        {/* DATA → UNDERSTANDING → DECISION flow */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-0">
          {FLOW_STEPS?.map((step, i) => (
            <React.Fragment key={step?.label}>
              <div
                className="flex flex-col items-center text-center px-6 py-5 rounded-2xl"
                style={{
                  background: flowVisible ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.4)',
                  border: '1px solid rgba(211,223,219,0.7)',
                  minWidth: '160px',
                  opacity: flowVisible ? 1 : 0,
                  transform: flowVisible ? 'translateY(0)' : 'translateY(12px)',
                  transition: `opacity 0.5s ease ${i * 150}ms, transform 0.5s cubic-bezier(0.22,1,0.36,1) ${i * 150}ms`,
                  boxShadow: i === 2 ? '0 8px 32px rgba(31,77,63,0.1)' : 'none',
                  borderColor: i === 2 ? 'rgba(31,77,63,0.2)' : 'rgba(211,223,219,0.7)',
                }}
              >
                <p
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    color: i === 2 ? '#1F4D3F' : '#0D2620',
                    textTransform: 'uppercase',
                    marginBottom: '6px',
                  }}
                >
                  {step?.label}
                </p>
                <p style={{ fontFamily: 'var(--font-sans)', fontSize: '12px', color: '#5D6B66', lineHeight: 1.5 }}>
                  {step?.sub}
                </p>
              </div>
              {i < FLOW_STEPS?.length - 1 && (
                <div
                  className="flex items-center justify-center w-10 h-10 flex-shrink-0"
                  style={{
                    opacity: flowVisible ? 1 : 0,
                    transition: `opacity 0.4s ease ${i * 150 + 200}ms`,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M4 10h12M11 5l5 5-5 5" stroke="#9AADA7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
